import { cn } from '@duck/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useRef, useEffect } from 'react'
import { X, type LucideIcon } from 'lucide-react'
import { TranslationKey, useTar } from '@/modules/i18n'
import { useTabStyle, type TabStyle } from './tabStyle'

// Shared tab styles - exported for use by AddTabButton
export const getTabClassName = (
    isActive: boolean,
    isDragging: boolean,
    isThin: boolean,
    className: string | undefined,
    tabStyle: TabStyle,
) => {
    const shared = cn(
        'relative h-8 cursor-pointer select-none',
        isThin ? 'ps-3 pe-2' : 'px-5',
        'flex items-center justify-center',
        'text-sm font-medium transition-colors outline-none',
        isDragging && 'cursor-grabbing',
        className,
    )

    if (tabStyle === 'boxes') {
        return cn(
            shared,
            'border border-border transition-colors',
            isActive
                ? [
                      // 31px (1px taller than inactive) + -mb-px so the extra px overlaps
                      // the content pane's top border. Top edges align with inactive tabs.
                      'h-[30px] text-foreground border-b-0 -mb-px',
                      // Active indicator: swap top border color to primary.
                      'border-t-primary',
                      'bg-card',
                  ]
                : [
                      'h-[29px] text-muted-foreground hover:text-foreground',
                      'bg-muted/20 hover:bg-muted/35 border-b-0',
                  ],
        )
    }

    if (tabStyle === 'underline') {
        return cn(
            shared,
            'before:absolute before:inset-x-0 before:bottom-0 before:h-0.5 before:transition-colors',
            isActive
                ? 'text-foreground before:bg-primary'
                : 'text-muted-foreground hover:text-foreground before:bg-transparent',
        )
    }

    // rounded (default)
    return cn(
      shared,
      'before:border-[1px] before:border-solid before:border-muted/60 before:border-b-0',
      'before:absolute before:inset-0 before:rounded-t-md',
      'before:[transform:perspective(8px)_rotateX(2deg)]',
      'before:origin-bottom before:transition-colors',
      'pt-1',
      isActive
        ? 'text-foreground before:bg-card before:border-t-primary before:border-l-primary before:border-r-primary before:border-t-[1px] before:border-l-[1px] before:border-r-[1px]'
        : 'before:bg-background before:hover:bg-muted/35 text-muted-foreground hover:text-foreground'
    )
}

export interface WorkspaceTabProps {
    sortableId: string
    label: TranslationKey
    Icon: LucideIcon
    iconColor: string
    index: number
    isActive: boolean
    isDragActive: boolean
    className?: string
    onActivateTab: () => void
    onRemoveTab?: (method?: 'button' | 'middle_click') => void
    tabRef?: (el: HTMLDivElement | null) => void
    disabled?: boolean
}

export const WorkspaceTab = ({
    sortableId,
    label,
    Icon,
    iconColor,
    index,
    isActive,
    isDragActive,
    className,
    onActivateTab,
    onRemoveTab,
    tabRef,
    disabled,
}: WorkspaceTabProps) => {
    const wasDragActiveRef = useRef(false)
    const { t } = useTar()
    const tabStyle = useTabStyle()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useSortable({
        id: sortableId,
        disabled,
    })

    const justDropped = wasDragActiveRef.current && !isDragActive

    useEffect(() => {
        wasDragActiveRef.current = isDragActive
    }, [isDragActive])

    const style = {
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: justDropped ? 'none' : undefined,
        zIndex: isDragging ? 200 : 100 - index + (isActive ? 100 : 0),
        opacity: isDragging ? 0 : 1,
    }

    return (
        <div
            ref={(el) => {
                setNodeRef(el)
                tabRef?.(el)
            }}
            style={style}
            {...attributes}
            {...listeners}
            role="tab"
            tabIndex={0}
            aria-label={t(label)}
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            className={cn('group', getTabClassName(isActive, isDragging, false, className, tabStyle), onRemoveTab && 'pe-2')}
            onClick={onActivateTab}
            onAuxClick={(e) => {
                if (e.button === 1 && onRemoveTab) {
                    e.preventDefault()
                    onRemoveTab('middle_click')
                }
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onActivateTab()
                }
            }}
        >
            <span className="relative z-10 flex items-center gap-1.5 whitespace-nowrap">
                <Icon className={cn('size-3.5 shrink-0 me-1', iconColor)} aria-hidden />
                {t(label)}
            </span>
            {onRemoveTab && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('common.close')}
                    className="relative z-10 mx-1 mt-0 p-0.5 rounded-xl opacity-0 group-hover:opacity-100 hover:bg-muted/60 transition-all"
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemoveTab?.('button')
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                            e.preventDefault()
                            onRemoveTab?.('button')
                        }
                    }}
                >
                    <X className="size-3" />
                </span>
            )}
        </div>
    )
}

// Static overlay component for DragOverlay - not affected by layout shifts
export interface WorkspaceTabOverlayProps {
    label: TranslationKey
    Icon: LucideIcon
    iconColor: string
    isActive: boolean
    className?: string
}

export const WorkspaceTabOverlay = ({
    label,
    Icon,
    iconColor,
    isActive,
    className,
}: WorkspaceTabOverlayProps) => {
    const { t } = useTar()
    const tabStyle = useTabStyle()
    return (
        <div
            className={cn(getTabClassName(isActive, true, false, className, tabStyle), 'pe-2', !isActive && 'text-foreground')}
            style={{ zIndex: 300 }}
        >
            <span className="relative z-10 flex items-center gap-1.5 whitespace-nowrap">
                <Icon className={cn('size-3.5 shrink-0 me-1', iconColor)} aria-hidden />
                {t(label)}
            </span>
            <span
                aria-hidden
                className="relative z-10 mx-1 mt-0 p-0.5 rounded-xl"
            >
                <X className="size-3" />
            </span>
        </div>
    )
}
